import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AnchorLink } from "@/components/AnchorLink";
import { usePageMeta } from "@/lib/meta";

const SECTIONS = [
  {
    title: "What we collect",
    body: "When you join the Peanut Valet update list, we save your email address and, if you choose to share it, your first name. When you send a partner inquiry, we save your name, email address, company, area of interest and message.",
  },
  {
    title: "How we use it",
    body: "We use your email address to send you Peanut Valet development updates, launch plans and news about opportunities to support the journey. Partner inquiry details are used only to respond to your inquiry and continue the conversation. We do not sell your information or share it with third parties for their own marketing.",
  },
  {
    title: "How it's stored",
    body: "Submissions are saved in a secure database connected to this website, with access limited to the Peanut Valet team.",
  },
  {
    title: "Analytics",
    body: "This site uses basic website analytics to understand visits and improve the experience. Analytics information is used in aggregate and is not used to advertise to you elsewhere.",
  },
  {
    title: "Your choices",
    body: "Every update email includes an unsubscribe option, and you can unsubscribe anytime. You can also ask us to update or delete your information at any time by reaching out through the Partner With Us form on our home page.",
  },
  {
    title: "Changes to this policy",
    body: "If we make meaningful changes to this privacy information, we'll post the update on this page.",
  },
];

export default function PrivacyPage() {
  usePageMeta({
    title: "Privacy Policy | Peanut Valet",
    description: "Learn how Peanut Valet handles information submitted through its email update signup and partnership inquiry forms.",
    canonical: "https://peanutvalet.com/privacy",
    ogImage: "https://peanutvalet.com/assets/peanut-valet-social-share.png",
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page">
      <Header />
      <main className="bg-[#F7F1E6] pb-24 pt-32 sm:pt-36">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h1 className="text-[2.2rem] font-extrabold tracking-tight text-[#074345] sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-3 text-sm font-semibold text-[#506460]">Last updated: July 2026</p>
          <p className="mt-6 text-base leading-relaxed text-[#243C39] sm:text-lg">
            Peanut Valet is a product in development, and this website exists to share
            the vision and keep interested people informed. Here&rsquo;s how information
            submitted through this site is handled.
          </p>

          <div className="mt-12 space-y-10">
            {SECTIONS.map((s) => (
              <section key={s.title} data-testid={`privacy-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h2 className="text-xl font-extrabold text-[#074345] sm:text-2xl">{s.title}</h2>
                <p className="mt-3 text-base leading-relaxed text-[#243C39]">{s.body}</p>
              </section>
            ))}
          </div>

          <div className="mt-14 rounded-[2rem] bg-[#EADCC6] p-8">
            <h2 className="text-xl font-extrabold text-[#074345]">Questions about your information?</h2>
            <p className="mt-3 text-base leading-relaxed text-[#243C39]">
              Reach us anytime through the{" "}
              <AnchorLink
                to="#partner"
                testId="privacy-contact-link"
                className="font-bold text-[#074345] underline decoration-[#F3802A] decoration-2 underline-offset-4 hover:text-[#115B5B]"
              >
                Partner With Us form
              </AnchorLink>{" "}
              on the home page.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
